import { queryClient } from "@/constants"
import { CLIENT_API } from "@/services/axios-client"
import { useNotificationStore } from "@/states/zustand/notification"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useEffect } from "react"

export const useGetContacts = () => {

    const { data, isLoading, error, ...rest } = useQuery({
        queryKey: ["contacts"],
        queryFn: () => CLIENT_API.getContacts(),
    })

    useEffect(() => {
        if (error) {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error instanceof Error ? error.message : "Failed to fetch contacts"
            });
        }
    }, [error])


    return {
        contacts: data,
        isLoading,
        ...rest
    }
}

export const useCreateContact = () => {
    const { mutate, isPending } = useMutation({
        mutationKey: ["createContact"],
        mutationFn: (data: CreateContactRequest) => CLIENT_API.createContact(data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["contacts"]
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Contact created successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to create contact"
            })
        }
    });

    return {
        mutate,
        isLoading: isPending
    }
}

export const useUpdateContact = () => {
    const { mutate, isPending } = useMutation({
        mutationKey: ["updateContact"],
        mutationFn: (payload: { id: number, data: Partial<CreateContactRequest> }) => CLIENT_API.updateContact(payload.id, payload.data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["contacts"]
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Contact updated successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to update contact"
            })
        }
    });

    return {
        mutate,
        isLoading: isPending
    }
}

export const useDeleteContacts = () => {
    const { mutate, isPending, ...rest } = useMutation({
        mutationKey: ["deleteContact"],
        mutationFn: (ids: number[]) => CLIENT_API.deleteContacts({ ids }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["contacts"]
            });
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Contacts deleted successfully!"
            })
        },
        onError: (error: string) => {
            useNotificationStore.getState().setNotification({
                type: "error",
                message: error || "Failed to delete contacts!"
            })
        }
    });

    return {
        mutate,
        isLoading: isPending,
        ...rest
    }
}